import { useId } from "@floating-ui/react"
import classNames from "classnames"
import type {
  ComponentPropsWithoutRef,
  ReactNode,
  Ref,
} from "react"
import {
  forwardRef,
  useLayoutEffect,
} from "react"
import styles from "./LargeDropdown.module.scss"
import { useDropdownContext } from "src/inputs/Dropdown/DropdownContext"
import { useDropdownOptionAttributesContext } from "src/inputs/Dropdown/DropdownOptionAttributesContext"
import { useDropdownSelectContext } from "src/inputs/Dropdown/DropdownSelectContext"

export interface Props extends ComponentPropsWithoutRef<"option"> {
  value: number | string;
  // what to show when this value is selected, defaults to props.children
  currentSelectionChildren?: ReactNode;
}

/*
 * renders an option for the large dropdown select menu
 */
export const LargeDropdownOption = forwardRef<HTMLOptionElement, Props>(
  function LargeDropdownOption(props: Props, propRef: Ref<HTMLOptionElement>) {
    const {
      children,
      currentSelectionChildren = children,
      value,
      disabled = false,
      ...htmlOptionProps
    } = props

    const id = useId()

    const { currentValue } = useDropdownSelectContext()
    const { setCurrentSelectionId } = useDropdownContext()

    const {
      registerOptionAttributes,
      deregisterOptionAttributes,
    } = useDropdownOptionAttributesContext()

    // register this options attributes to the context
    useLayoutEffect(
      () => {
        registerOptionAttributes(
          id,
          {
            children: currentSelectionChildren,
            disabled,
          },
        )

        return () => {
          deregisterOptionAttributes(id)
        }
      },
      [
        currentSelectionChildren,
        disabled,
        id,
        registerOptionAttributes,
        deregisterOptionAttributes,
      ],
    )

    useLayoutEffect(
      () => {
        if (value === currentValue) {
          setCurrentSelectionId(id)
        }
      },
      [
        id,
        value,
        currentValue,
        setCurrentSelectionId,
      ],
    )

    const className = classNames(
      styles.largeDropdownOption,
      props.className,
    )

    return (
      <option
        {...htmlOptionProps}
        className={className}
        data-id={id}
        disabled={disabled}
        ref={propRef}
        value={value}
      >
        {children}
      </option>
    )
  },
)
